import {
  memo,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
  useId,
  type KeyboardEvent,
} from "react";
import { useTheme } from "@repo/tokens";
import { Icon } from "../icon";
import { useAccordion } from "./useAccordion";
import {
  AccordionDispatchContext,
  AccordionStateContext,
  AccordionItemContext,
  useAccordionDispatch,
  useAccordionState,
  useAccordionItemContext,
} from "./AccordionContext";
import { useStyles, TRANSITION_MS } from "./styles";
import type {
  AccordionProps,
  AccordionItemProps,
  AccordionTriggerProps,
  AccordionContentProps,
} from "./types";

// ─── Accordion (root) ────────────────────────────────────────────────

export function Accordion({
  type = "single",
  value,
  onChange,
  defaultValue,
  collapsible = true,
  children,
  style,
  className,
}: AccordionProps) {
  const { expandedItems, toggleItem } = useAccordion({
    type,
    value,
    onChange,
    defaultValue,
    collapsible,
  });
  const styles = useStyles();

  // Split context: dispatch is stable, state changes on toggle
  const dispatchValue = useMemo(
    () => ({ toggleItem, type }),
    [toggleItem, type],
  );
  const stateValue = useMemo(() => ({ expandedItems }), [expandedItems]);

  return (
    <AccordionDispatchContext.Provider value={dispatchValue}>
      <AccordionStateContext.Provider value={stateValue}>
        <div
          data-accordion-root=""
          className={className}
          style={{ ...styles.root, ...style }}
        >
          {children}
        </div>
      </AccordionStateContext.Provider>
    </AccordionDispatchContext.Provider>
  );
}

// ─── AccordionItem ───────────────────────────────────────────────────

export const AccordionItem = memo(function AccordionItem({
  value,
  disabled = false,
  children,
  style,
  className,
}: AccordionItemProps) {
  const { expandedItems } = useAccordionState();
  const isExpanded = expandedItems.has(value);
  const styles = useStyles();

  // Stable unique IDs — useId guarantees uniqueness even if values collide
  const reactId = useId();
  const triggerId = `accordion-trigger-${reactId}`;
  const contentId = `accordion-content-${reactId}`;

  const itemContext = useMemo(
    () => ({ value, isExpanded, disabled, triggerId, contentId }),
    [value, isExpanded, disabled, triggerId, contentId],
  );

  return (
    <AccordionItemContext.Provider value={itemContext}>
      <div className={className} style={{ ...styles.item, ...style }}>
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
});

// ─── AccordionTrigger ────────────────────────────────────────────────

export const AccordionTrigger = memo(function AccordionTrigger({
  children,
  hideIcon = false,
  icon,
  style,
  className,
}: AccordionTriggerProps) {
  const { toggleItem } = useAccordionDispatch();
  const { value, isExpanded, disabled, triggerId, contentId } =
    useAccordionItemContext();
  const { theme } = useTheme();
  const styles = useStyles();

  const handleClick = useCallback(() => {
    if (!disabled) {
      toggleItem(value);
    }
  }, [disabled, toggleItem, value]);

  /**
   * Keyboard navigation per WAI-ARIA Accordion pattern:
   * - Enter/Space: toggle current item
   * - ArrowDown: focus next trigger
   * - ArrowUp: focus previous trigger
   * - Home: focus first trigger
   * - End: focus last trigger
   */
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLButtonElement>) => {
    const trigger = e.currentTarget;
    const accordion = trigger.closest("[data-accordion-root]");
    if (!accordion) return;

    const triggers = Array.from(
      accordion.querySelectorAll<HTMLButtonElement>(
        "[data-accordion-trigger]:not(:disabled)",
      ),
    );
    const currentIndex = triggers.indexOf(trigger);
    if (currentIndex === -1) return;

    let targetIndex: number | null = null;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        targetIndex = (currentIndex + 1) % triggers.length;
        break;
      case "ArrowUp":
        e.preventDefault();
        targetIndex = (currentIndex - 1 + triggers.length) % triggers.length;
        break;
      case "Home":
        e.preventDefault();
        targetIndex = 0;
        break;
      case "End":
        e.preventDefault();
        targetIndex = triggers.length - 1;
        break;
    }

    if (targetIndex !== null) {
      triggers[targetIndex].focus();
    }
  }, []);

  return (
    <button
      type="button"
      data-accordion-trigger=""
      aria-expanded={isExpanded}
      aria-controls={contentId}
      id={triggerId}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
      style={{
        ...styles.trigger,
        ...(disabled ? styles.triggerDisabled : {}),
        ...style,
      }}
    >
      <span style={styles.triggerContent}>{children}</span>
      {!hideIcon && (
        <span style={styles.chevron(isExpanded)}>
          {icon ?? (
            <Icon
              name="expandMore"
              size={20}
              color={theme.colors.neutral[40]}
            />
          )}
        </span>
      )}
    </button>
  );
});

// ─── Lazy mount lifecycle ────────────────────────────────────────────

/**
 * Two-phase mount/unmount for lazy content:
 *
 * Expanding:  mount DOM at 0fr → next frame → animate to 1fr
 * Collapsing: animate to 0fr → wait for transition → unmount DOM
 *
 * Without this, lazy content pops in at full height (no "from" state
 * for the CSS grid transition) causing layout shift and scrollbar jiggle.
 */
function useLazyMount(isExpanded: boolean, transitionMs: number) {
  const [shouldRender, setShouldRender] = useState(isExpanded);
  const [animateOpen, setAnimateOpen] = useState(isExpanded);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const rafRef = useRef<number>(undefined);

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    if (isExpanded) {
      setShouldRender(true);
      // Double rAF: first ensures mount paint, second triggers transition
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => setAnimateOpen(true));
      });
    } else {
      setAnimateOpen(false);
      timeoutRef.current = setTimeout(
        () => setShouldRender(false),
        transitionMs,
      );
    }

    return () => {
      clearTimeout(timeoutRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isExpanded, transitionMs]);

  return { shouldRender, animateOpen };
}

export const AccordionContent = memo(function AccordionContent({
  children,
  lazy = false,
  style,
  className,
  styleOnExpanded,
  styleContentInner,
  contentOverflowStyles,
}: AccordionContentProps) {
  const { isExpanded, triggerId, contentId } = useAccordionItemContext();
  const styles = useStyles();
  const { shouldRender, animateOpen } = useLazyMount(isExpanded, TRANSITION_MS);

  if (lazy && !shouldRender) return null;

  const gridExpanded = lazy ? animateOpen : isExpanded;

  return (
    <div
      role="region"
      id={contentId}
      aria-labelledby={triggerId}
      aria-hidden={!isExpanded}
      className={className}
      style={{
        ...styles.contentGrid(gridExpanded),
        ...style,
        ...(isExpanded ? styleOnExpanded : {}),
      }}
    >
      <div
        style={{
          ...styles.contentOverflow,
          ...(isExpanded && contentOverflowStyles),
        }}
      >
        <div style={{ ...styles.contentInner, ...styleContentInner }}>
          {children}
        </div>
      </div>
    </div>
  );
});

export type {
  AccordionProps,
  AccordionItemProps,
  AccordionTriggerProps,
  AccordionContentProps,
} from "./types";
